// The magic link's API (`apps/server/src/http/meeting-links.ts`).
//
// Every call sends the token as `Authorization: LoomLink <token>` and NO
// cookie (`credentials: "omit"`): the holder acts as the meeting's owner, and
// whoever is signed in to Loom in this browser has nothing to do with it.
// The token never goes into a URL or a log. Every answer is parsed with the
// shared allowlist DTO (`@loom/shared/meetings`).
//
// Any refusal of the token (unknown, expired, revoked, the owner no longer
// approved) is one 404 `not_found`.

import {
  MeetingLinkIssueDto,
  MeetingLinkReviewDto,
  type MeetingAnswerRequest,
  type MeetingIssuePatchRequest,
} from "@loom/shared/meetings";

import { httpGet, httpPatch, httpPost, httpPut } from "../../lib/http";

export type LinkAction = "accept" | "decline";

const init = (token: string, json: boolean): RequestInit => ({
  credentials: "omit",
  cache: "no-store",
  headers: {
    accept: "application/json",
    ...(json ? { "content-type": "application/json" } : {}),
    authorization: `LoomLink ${token}`,
  },
});

export const readLinkReview = (token: string): Promise<MeetingLinkReviewDto> =>
  httpGet("/api/meeting-links/review", MeetingLinkReviewDto, init(token, false));

export const linkIssueAction = (
  token: string,
  iid: number,
  action: LinkAction,
): Promise<MeetingLinkIssueDto> =>
  httpPost(`/api/meeting-links/issues/${iid}/${action}`, {}, MeetingLinkIssueDto, init(token, true));

export const editLinkIssue = (
  token: string,
  iid: number,
  patch: MeetingIssuePatchRequest,
): Promise<MeetingLinkIssueDto> =>
  httpPatch(`/api/meeting-links/issues/${iid}`, patch, MeetingLinkIssueDto, init(token, true));

/** An open question answered through the link, or "Weiss ich nicht" (refine light). */
export const answerLinkQuestion = (
  token: string,
  iid: number,
  field: string,
  body: MeetingAnswerRequest,
): Promise<MeetingLinkIssueDto> =>
  httpPut(
    `/api/meeting-links/issues/${iid}/questions/${encodeURIComponent(field)}`,
    body,
    MeetingLinkIssueDto,
    init(token, true),
  );

/** The query key the token rides under in the fragment: `/m#k=<token>`. */
export const LINK_TOKEN_KEY = "k";

/** The token in a `#k=…` fragment, or null. `URLSearchParams`, no regex. */
export const linkTokenFromHash = (hash: string): string | null => {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  const token = new URLSearchParams(raw).get(LINK_TOKEN_KEY);
  return token !== null && token.length > 0 ? token : null;
};

/** The link a token makes, on this origin. */
export const meetingLinkUrl = (origin: string, token: string): string =>
  `${origin}/m#${new URLSearchParams({ [LINK_TOKEN_KEY]: token }).toString()}`;
